import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { fmtRp } from "@/lib/utils";
import { amountOf } from "@/components/printDocs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loading } from "@/components/bits";

/** Pilihan jenis cetak SPK: Operasional (tanpa harga) / Komersial (dengan harga) */
export function PrintModeToggle({ commercial, onChange, testid = "spk-print-mode" }) {
  return (
    <div className="inline-flex overflow-hidden rounded border border-slate-600 bg-slate-800 text-xs shadow-lg" data-testid={testid}>
      {[[false, "Operasional (tanpa harga)"], [true, "Komersial (dengan harga)"]].map(([v, t]) => (
        <button key={String(v)} type="button" onClick={() => onChange(v)} className={`px-3 py-2 transition-colors ${commercial === v ? "bg-amber-500 font-semibold text-[#0B1220]" : "text-slate-200 hover:bg-slate-700"}`} data-testid={`${testid}-${v ? "komersial" : "operasional"}`}>{t}</button>
      ))}
    </div>
  );
}

const UNIT_CLS = (u) => (u === "METER" ? "border-violet-500/50 bg-violet-500/15 text-violet-200" : "border-sky-500/50 bg-sky-500/15 text-sky-200");

/** Dialog admin: atur unit & harga satuan per marking dalam satu SPK (+ terapkan ke semua) */
export function SpkHargaDialog({ spk, onClose, onSaved }) {
  const [rows, setRows] = useState(null);
  const [bulk, setBulk] = useState({ unitHarga: "TON", hargaSatuan: String(spk?.rateTon || "") });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!spk) return;
    api.get(`/markings?spkId=${spk.id}&limit=1000`).then((r) => setRows(r.data.map((m) => ({ ...m, _unit: m.unitHarga || "TON", _harga: String(m.hargaSatuan ?? spk.rateTon ?? 0) })))).catch((e) => toast.error(fmtErr(e)));
  }, [spk]);

  if (!spk) return null;
  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const total = (r) => amountOf(r.realisasi || 0, (r.unitTon || 0) * 1000, r.panjang, r._unit, Number(r._harga) || 0);
  const nilai = (r) => amountOf(r.qty || 0, (r.unitTon || 0) * 1000, r.panjang, r._unit, Number(r._harga) || 0);

  const applyAll = async () => {
    setBusy(true);
    try {
      await api.patch(`/spk/${spk.id}/harga-marking`, { unitHarga: bulk.unitHarga, hargaSatuan: Number(bulk.hargaSatuan) || 0 });
      setRows((rs) => rs.map((r) => ({ ...r, _unit: bulk.unitHarga, _harga: String(Number(bulk.hargaSatuan) || 0) })));
      toast.success(`Harga diterapkan ke semua marking ${spk.no}`);
      onSaved?.();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  const saveAll = async () => {
    setBusy(true);
    const changed = rows.filter((r) => r._unit !== (r.unitHarga || "TON") || Number(r._harga) !== Number(r.hargaSatuan ?? spk.rateTon ?? 0));
    try {
      await Promise.all(changed.map((r) => api.patch(`/markings/${r.id}/harga`, { unitHarga: r._unit, hargaSatuan: Number(r._harga) || 0 })));
      toast.success(changed.length ? `${changed.length} marking diperbarui` : "Tidak ada perubahan");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto border-slate-800 bg-[#0D1524] text-slate-100" data-testid="spk-harga-dialog">
        <DialogHeader><DialogTitle className="font-display">Harga Marking · <span className="font-mono text-sky-400">{spk.no}</span> <span className="cn text-xs font-normal text-slate-500">构件单价</span></DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-end gap-2 rounded border border-slate-800 bg-slate-900/60 p-3 text-xs" data-testid="spk-harga-bulk">
          <div>
            <div className="mb-1 text-[10px] text-slate-500">Unit harga</div>
            <div className="inline-flex overflow-hidden rounded border border-slate-700">
              {["TON", "METER"].map((u) => <button key={u} type="button" onClick={() => setBulk({ ...bulk, unitHarga: u })} className={`px-3 py-1.5 ${bulk.unitHarga === u ? UNIT_CLS(u) : "text-slate-400 hover:text-slate-200"}`} data-testid={`spk-harga-bulk-unit-${u.toLowerCase()}`}>{u}</button>)}
            </div>
          </div>
          <div>
            <div className="mb-1 text-[10px] text-slate-500">Harga satuan (Rp / {bulk.unitHarga === "METER" ? "meter" : "ton"})</div>
            <Input type="number" min="0" value={bulk.hargaSatuan} onChange={(e) => setBulk({ ...bulk, hargaSatuan: e.target.value })} className="h-8 w-40 border-slate-800 bg-slate-950 font-mono text-xs" data-testid="spk-harga-bulk-input" />
          </div>
          <Button size="sm" disabled={busy} onClick={applyAll} variant="outline" className="h-8 border-amber-500/40 text-amber-300 hover:bg-amber-500/10" data-testid="spk-harga-apply-all">Terapkan ke semua marking</Button>
          <span className="ml-auto text-[11px] text-slate-500">TON: unit berat × qty ÷ 1000 × harga · METER: qty × panjang(mm) ÷ 1000 × harga</span>
        </div>
        {!rows ? <Loading /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="spk-harga-table">
              <thead><tr className="text-[10px] text-slate-500">
                <th className="py-1 text-left font-normal">Marking</th><th className="py-1 text-left font-normal">Profile</th><th className="py-1 text-right font-normal">Panjang (mm)</th><th className="py-1 text-right font-normal">Unit (kg)</th><th className="py-1 text-right font-normal">Qty</th><th className="py-1 text-right font-normal">Selesai</th>
                <th className="py-1 text-center font-normal">Unit Harga</th><th className="py-1 text-right font-normal">Harga Satuan</th><th className="py-1 text-right font-normal">Nilai SPK</th><th className="py-1 text-right font-normal">Total Harga (selesai)</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-800/60" data-testid={`spk-harga-row-${r.code}`}>
                    <td className="py-1.5 font-mono text-amber-400">{r.code}</td>
                    <td className="py-1.5 text-slate-400">{r.profile || "—"}</td>
                    <td className="py-1.5 text-right font-mono">{r.panjang != null ? Number(r.panjang).toLocaleString("id-ID") : "—"}</td>
                    <td className="py-1.5 text-right font-mono text-slate-400">{((r.unitTon || 0) * 1000).toLocaleString("id-ID", { maximumFractionDigits: 2 })}</td>
                    <td className="py-1.5 text-right font-mono">{r.qty}</td>
                    <td className="py-1.5 text-right font-mono text-cyan-300">{r.realisasi || 0}</td>
                    <td className="py-1.5 text-center">
                      <button type="button" onClick={() => setRow(r.id, { _unit: r._unit === "TON" ? "METER" : "TON" })} className={`rounded border px-2 py-0.5 text-[10px] ${UNIT_CLS(r._unit)}`} data-testid={`spk-harga-unit-${r.code}`}>{r._unit}</button>
                    </td>
                    <td className="py-1.5 text-right"><Input type="number" min="0" value={r._harga} onChange={(e) => setRow(r.id, { _harga: e.target.value })} className="ml-auto h-7 w-32 border-slate-800 bg-slate-950 text-right font-mono text-xs" data-testid={`spk-harga-input-${r.code}`} /></td>
                    <td className="py-1.5 text-right font-mono text-slate-300">{fmtRp(nilai(r))}</td>
                    <td className="py-1.5 text-right font-mono text-slate-100" data-testid={`spk-harga-total-${r.code}`}>{fmtRp(total(r))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t border-slate-700 text-[11px]">
                <td colSpan={8} className="py-2 text-right text-slate-400">Total</td>
                <td className="py-2 text-right font-mono text-slate-200" data-testid="spk-harga-sum-nilai">{fmtRp(rows.reduce((a, r) => a + nilai(r), 0))}</td>
                <td className="py-2 text-right font-mono font-semibold text-sky-300" data-testid="spk-harga-sum-total">{fmtRp(rows.reduce((a, r) => a + total(r), 0))}</td>
              </tr></tfoot>
            </table>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="border-slate-700" onClick={onClose} data-testid="spk-harga-cancel">Tutup</Button>
          <Button disabled={busy || !rows} onClick={saveAll} className="bg-sky-600 hover:bg-sky-500" data-testid="spk-harga-save">Simpan Perubahan</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
